'use client';

import { Icon } from '@/components/ds/Icon';
import { Badge } from '@/components/ds/Badge';
import { Button } from '@/components/ds/Button';
import { Card } from '@/components/ds/Card';

// ---------------------------------------------------------------------------
// LockedModule — generic locked-feature teaser panel
// ---------------------------------------------------------------------------

export interface LockedModuleProps {
  /** Feature heading shown in the panel. */
  title: string;
  /** Supporting copy under the heading. */
  description: string;
  /** Pro or Premium tier badge. */
  tier: 'Pro' | 'Premium';
}

/**
 * Static teaser for a locked Pro/Premium feature.
 * Renders a champagne lock icon, tier Badge, title, description, and a
 * non-functional upgrade CTA button. No backend calls.
 */
export function LockedModule({ title, description, tier }: LockedModuleProps) {
  return (
    <div
      style={{
        padding: '2rem 1.5rem',
        maxWidth: 560,
        margin: '0 auto',
        display: 'flex',
        flexDirection: 'column',
        gap: '1.5rem',
      }}
    >
      <Card padding={32}>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 16,
            textAlign: 'center',
          }}
        >
          {/* Lock icon in champagne wash circle */}
          <div
            style={{
              width: 64,
              height: 64,
              borderRadius: 'var(--r-full)',
              background: 'var(--champagne-wash)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <Icon name="lock" size={28} color="var(--champagne-500)" />
          </div>

          <Badge tone="premium">{tier}</Badge>

          <h2
            style={{
              fontFamily: 'var(--font-sans)',
              fontSize: 'var(--fs-title2)',
              fontWeight: 700,
              color: 'var(--text-primary)',
              margin: 0,
              lineHeight: 'var(--lh-title2)',
            }}
          >
            {title}
          </h2>

          <p
            style={{
              fontFamily: 'var(--font-sans)',
              fontSize: 'var(--fs-body)',
              color: 'var(--text-secondary)',
              margin: 0,
              maxWidth: 360,
              lineHeight: 'var(--lh-body)',
            }}
          >
            {description}
          </p>

          {/* Static upgrade CTA — non-functional, no backend */}
          <Button variant="primary" size="md" disabled>
            {`שדרג/י ל-${tier}`}
          </Button>
        </div>
      </Card>
    </div>
  );
}
